import { db } from "../firebase";
import app from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

const functions = getFunctions(app, "us-central1");

const callMailTaskAction = async (mailTaskAction, data = {}) => {
  const callable = httpsCallable(functions, "createUser");
  return (await callable({ mailTaskAction, ...data })).data;
};

export const MAIL_TASK_STATUS = {
  DRAFT: "draft",
  CONVERTED: "converted",
  ARCHIVED: "archived"
};

const normalizeTagKey = (value = "") =>
  String(value)
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replaceAll("ı", "i")
    .replaceAll("İ", "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_-]/g, "");

const normalizeText = (value = "") =>
  String(value)
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replaceAll("ı", "i")
    .replaceAll("İ", "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");

const cleanForwardedSubject = (subject = "") =>
  String(subject || "")
    .replace(/^\s*(fw|fwd|re|ynt|cevap)\s*:\s*/i, "")
    .trim();

const parseDateTag = (value = "") => {
  const text = String(value || "").trim();
  if (!text) return "";

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) return text;

  const dottedMatch = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (dottedMatch) {
    const day = dottedMatch[1].padStart(2, "0");
    const month = dottedMatch[2].padStart(2, "0");
    const year = dottedMatch[3];

    return `${year}-${month}-${day}`;
  }

  return text;
};

const normalizePriority = (value = "") => {
  const text = normalizeText(value);

  if (!text) return "normal";
  if (["acil", "urgent", "kritik", "critical"].some((item) => text.includes(item))) return "acil";
  if (["yuksek", "yüksek", "high"].some((item) => text.includes(item))) return "yüksek";
  if (["dusuk", "düşük", "low"].some((item) => text.includes(item))) return "düşük";

  return "normal";
};

export const parseMailTaskTags = ({ subject = "", body = "" } = {}) => {
  const lines = String(body || "").split(/\r?\n/);
  const scannedLines = lines.slice(0, 40);
  const cleanBodyLines = [];
  const tags = {
    company: "",
    responsible: "",
    dueDate: "",
    priority: "normal",
    project: "",
    note: "",
    notifyAll: false,
    steps: []
  };

  scannedLines.forEach((line) => {
    const trimmedLine = String(line || "").trim();
    const tagMatch = trimmedLine.match(/^#([\p{L}0-9_-]+)(?:\s*:\s*|\s+)?(.*)$/u);

    if (!tagMatch) return;

    const key = normalizeTagKey(tagMatch[1]);
    const value = String(tagMatch[2] || "").trim();

    if (["firma", "sirket", "şirket", "company", "musteri", "müşteri"].includes(key)) {
      tags.company = value;
      return;
    }

    if (["sorumlu", "atanan", "atama", "assignee", "owner"].includes(key)) {
      tags.responsible = value;
      return;
    }

    if (["tarih", "termin", "deadline", "due", "son"].includes(key)) {
      tags.dueDate = parseDateTag(value);
      return;
    }

    if (["oncelik", "öncelik", "priority"].includes(key)) {
      tags.priority = normalizePriority(value);
      return;
    }

    if (["acil", "urgent", "kritik"].includes(key)) {
      tags.priority = "acil";
      return;
    }

    if (["proje", "surec", "süreç", "pano", "project"].includes(key)) {
      tags.project = value;
      return;
    }

    if (["not", "note", "aciklama", "açıklama"].includes(key)) {
      tags.note = value;
      return;
    }

    if (["adim", "adım", "step", "todo"].includes(key)) {
      if (value) tags.steps.push(value);
      return;
    }

    if (["herkes", "all"].includes(key)) {
      tags.notifyAll = true;
    }
  });

  lines.forEach((line) => {
    const trimmedLine = String(line || "").trim();
    const tagMatch = trimmedLine.match(/^#([\p{L}0-9_-]+)(?:\s*:\s*|\s+)?(.*)$/u);

    if (!tagMatch) {
      cleanBodyLines.push(line);
      return;
    }

    const key = normalizeTagKey(tagMatch[1]);
    const knownKeys = [
      "firma", "sirket", "sirket", "company", "musteri",
      "sorumlu", "atanan", "atama", "assignee", "owner",
      "tarih", "termin", "deadline", "due", "son",
      "oncelik", "priority", "acil", "urgent", "kritik",
      "proje", "surec", "pano", "project",
      "not", "note", "aciklama",
      "adim", "step", "todo", "herkes", "all"
    ];

    if (!knownKeys.includes(key)) {
      cleanBodyLines.push(line);
    }
  });

  const cleanBody = cleanBodyLines.join("\n").trim();

  return {
    ...tags,
    title: cleanForwardedSubject(subject) || "Mailden gelen görev",
    cleanBody
  };
};

export const getMailTaskDrafts = async ({ currentUserEmail = "", isAdmin = false } = {}) => {
  const q = query(collection(db, "mailTaskDrafts"), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  const currentEmail = normalizeText(currentUserEmail);

  return snapshot.docs
    .map((docItem) => ({
      id: docItem.id,
      ...docItem.data()
    }))
    .filter((item) => {
      if (item.status === MAIL_TASK_STATUS.ARCHIVED) return false;
      if (isAdmin) return true;

      const createdByEmail = normalizeText(item.createdByEmail);
      const forwardedByEmail = normalizeText(item.forwardedByEmail);
      const responsibleEmail = normalizeText(item.responsibleEmail);

      return [createdByEmail, forwardedByEmail, responsibleEmail].includes(currentEmail);
    });
};

export const syncGmailTaskDrafts = async ({ maxResults = 20 } = {}) => {
  return callMailTaskAction("syncGmailInbox", { maxResults });
};

export const createManualMailTaskDraft = async ({
  subject = "",
  body = "",
  fromEmail = "",
  forwardedByEmail = "",
  forwardedByName = "",
  createdByUid = "",
  createdByEmail = "",
  createdByName = ""
}) => {
  const parsed = parseMailTaskTags({ subject, body });

  return addDoc(collection(db, "mailTaskDrafts"), {
    source: "manual",
    status: MAIL_TASK_STATUS.DRAFT,
    subject: subject || "",
    body: body || "",
    fromEmail: fromEmail || "",
    forwardedByEmail: forwardedByEmail || createdByEmail || "",
    forwardedByName: forwardedByName || createdByName || "",
    title: parsed.title,
    cleanBody: parsed.cleanBody,
    tagCompany: parsed.company,
    tagResponsible: parsed.responsible,
    tagDueDate: parsed.dueDate,
    tagPriority: parsed.priority,
    tagProject: parsed.project,
    tagNote: parsed.note,
    tagNotifyAll: parsed.notifyAll,
    tagSteps: parsed.steps,
    createdByUid: createdByUid || "",
    createdByEmail: createdByEmail || "",
    createdByName: createdByName || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateMailTaskDraft = async (draftId, data = {}) => {
  if (!draftId) {
    throw new Error("Mail görev taslağı bulunamadı.");
  }

  return updateDoc(doc(db, "mailTaskDrafts", draftId), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const markMailTaskDraftConverted = async ({ draftId, processId = "", convertedByEmail = "" }) => {
  if (!draftId) {
    throw new Error("Mail görev taslağı bulunamadı.");
  }

  return updateDoc(doc(db, "mailTaskDrafts", draftId), {
    status: MAIL_TASK_STATUS.CONVERTED,
    convertedProcessId: processId || "",
    convertedByEmail: convertedByEmail || "",
    convertedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const archiveMailTaskDraft = async (draftId) => {
  if (!draftId) {
    throw new Error("Mail görev taslağı bulunamadı.");
  }

  return updateDoc(doc(db, "mailTaskDrafts", draftId), {
    status: MAIL_TASK_STATUS.ARCHIVED,
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const deleteMailTaskDraft = async (draftId) => {
  if (!draftId) {
    throw new Error("Mail görev taslağı bulunamadı.");
  }

  return deleteDoc(doc(db, "mailTaskDrafts", draftId));
};
